import { collection, addDoc, doc, getDoc, setDoc } from "firebase/firestore";
import resolveDb from "../db";
import { firestoreDb } from "../firebase";
import {
	CODE_REQUESTED,
	DbInstance,
	LICENSE,
	LICENSE_EXPIRATION_DATE,
	SIGNED_IN,
} from "../utils/constants";
import isAfter from "date-fns/isAfter";
import format from "date-fns/format";
import { ErrorMessages, SignInError } from "../utils/errors";

const db = resolveDb(DbInstance.JSON_DB);

export async function requestCode(
	email: string | undefined,
	phone: string | undefined
) {
	try {
		await addDoc(collection(firestoreDb, "code_requests"), {
			email,
			phone,
			expirationDate: null,
			used: false,
			validated: false,
			sent: false,
		});
		await db.create(true, CODE_REQUESTED, { avoidUUID: true });

		return true;
	} catch {
		return Promise.reject(ErrorMessages[SignInError.REQUEST_CODE_FAIL]);
	}
}

export async function validateCode(code: string) {
	code = code.trim();

	try {
		const codeData = (
			await getDoc(doc(firestoreDb, "code_requests", code))
		).data();
		const validCode = Boolean(
			codeData &&
				!codeData?.validated &&
				!codeData?.used &&
				codeData?.expirationDate
		);

		if (validCode) {
			const dateString = format(codeData?.expirationDate.toDate(), "Pp");
			const expirationDate = new Date(dateString);
			const validLicense = isAfter(expirationDate, new Date());

			if (!validLicense) {
				return Promise.reject(ErrorMessages[SignInError.LICENSE_EXPIRED]);
			}

			await setDoc(
				doc(firestoreDb, "code_requests", code),
				{ validated: true },
				{ merge: true }
			);

			await Promise.all([
				db.create(validCode, SIGNED_IN, { avoidUUID: true }),
				db.create(dateString, LICENSE_EXPIRATION_DATE, {
					avoidUUID: true,
				}),
				db.create(code, LICENSE, {
					avoidUUID: true,
				}),
			]);

			return validCode;
		} else {
			if (!codeData) {
				return Promise.reject(ErrorMessages[SignInError.VALIDATE_CODE_FAIL]);
			}
			if (codeData?.validated) {
				return Promise.reject(
					ErrorMessages[SignInError.CODE_ALREADY_VALIDATED]
				);
			}
			if (!codeData?.expirationDate) {
				return Promise.reject(ErrorMessages[SignInError.LICENSE_EXPIRED]);
			}
			if (codeData?.used) {
				return Promise.reject(ErrorMessages[SignInError.CODE_ALREADY_USED]);
			}
		}
	} catch (e) {
		console.error("validateCode error:", e);
		return Promise.reject(ErrorMessages[SignInError.VALIDATE_CODE_FAIL]);
	}
}

export async function checkIsUserSignedIn() {
	const signedIn = await db.get(SIGNED_IN);

	if (!signedIn) {
		return signedIn;
	}

	const expirationDateStr = await db.get(LICENSE_EXPIRATION_DATE);

	if (expirationDateStr) {
		const expirationDate = new Date(expirationDateStr as string);
		const validLicense = isAfter(expirationDate, new Date());

		if (!validLicense) {
			await Promise.all([
				db.delete(SIGNED_IN),
				db.delete(CODE_REQUESTED),
				db.delete(LICENSE_EXPIRATION_DATE),
			]);

			return Promise.reject(ErrorMessages[SignInError.LICENSE_EXPIRED]);
		}

		return validLicense;
	}

	return Promise.reject(ErrorMessages[SignInError.MISSING_LICENSE_DATE]);
}

export async function checkIsCodeRequested() {
	const result = await db.get(CODE_REQUESTED);

	return result;
}
